import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ClockIcon, MapPinIcon, TruckIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { ordersAPI } from '../../services/api';

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const riderIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#009FE3" width="32" height="32">
      <circle cx="12" cy="12" r="10" fill="#009FE3"/>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

const pickupIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#F15A29" width="32" height="32">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const dropoffIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#10B981" width="32" height="32">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

interface ETAInfo {
  distance_km: number;
  duration_minutes: number;
  duration_in_traffic_minutes: number;
  eta_timestamp: string;
}

interface TrackingData {
  order_id: number;
  order_status: string;
  rider_location?: { lat: number; lng: number };
  rider_location_age_seconds?: number;
  pickup_location: { lat: number; lng: number; address: string };
  dropoff_location: { lat: number; lng: number; address: string };
  rider_to_pickup_eta?: ETAInfo;
  rider_to_dropoff_eta?: ETAInfo;
  total_distance_km?: number;
  rider_info?: { name: string; phone: string; vehicle_type: string };
  last_updated: string;
}

interface LiveTrackingProps {
  orderId: number;
  autoRefresh?: boolean;
  refreshInterval?: number; // milliseconds
  liveRiderPosition?: { lat: number; lng: number } | null;
}

// Component to auto-center map when data changes
const MapController: React.FC<{ center: [number, number]; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, zoom, { animate: true });
  }, [map, center, zoom]);
  
  return null;
};

const LiveTracking: React.FC<LiveTrackingProps> = ({
  orderId,
  autoRefresh = true,
  refreshInterval = 30000, // 30 seconds default
  liveRiderPosition = null,
}) => {
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [riderPosition, setRiderPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'slow' | 'offline'>('connected');

  // Update rider position from live WebSocket updates
  useEffect(() => {
    if (liveRiderPosition) {
      setRiderPosition(liveRiderPosition);
      setLastUpdateTime(new Date());
      console.log('📍 Updated rider position from WebSocket:', liveRiderPosition);
    }
  }, [liveRiderPosition]);

  const fetchTrackingData = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);

    try {
      const response = await ordersAPI.getOrderTracking(orderId);
      setTrackingData(response);
      setLastUpdateTime(new Date());
      setConnectionStatus('connected');
      
      // Initialize rider position from tracking data only if no live position
      if (!liveRiderPosition && response.rider_location) {
        setRiderPosition(response.rider_location);
      }
    } catch (err: any) {
      console.error('Error fetching tracking data:', err);
      setError(err.response?.data?.detail || 'Failed to load tracking information');
      setConnectionStatus('offline');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrackingData();

    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchTrackingData(false); // Don't show loading spinner on auto-refresh
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [orderId, autoRefresh, refreshInterval]);

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      assigned: 'bg-blue-100 text-blue-800 border-blue-200',
      accepted: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatETA = (minutes: number): string => {
    if (minutes < 1) return 'Less than 1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const formatDistance = (km: number): string => {
    if (km < 1) return `${Math.round(km * 1000)}m`;
    return `${km.toFixed(1)} km`;
  };

  const getMapCenter = (): [number, number] => {
    if (!trackingData) return [9.0820, 8.6753]; // Default to Abuja, Nigeria

    // If rider position exists (from WebSocket or initial data), center on rider
    if (riderPosition) {
      return [riderPosition.lat, riderPosition.lng];
    }

    // Otherwise center between pickup and dropoff
    const midLat = (trackingData.pickup_location.lat + trackingData.dropoff_location.lat) / 2;
    const midLng = (trackingData.pickup_location.lng + trackingData.dropoff_location.lng) / 2;
    return [midLat, midLng];
  };

  const getRoutePolyline = (): [number, number][] => {
    if (!trackingData) return [];

    const points: [number, number][] = [];

    // If rider is en route to pickup (use live position)
    if (riderPosition && ['assigned', 'accepted'].includes(trackingData.order_status)) {
      points.push([riderPosition.lat, riderPosition.lng]);
      points.push([trackingData.pickup_location.lat, trackingData.pickup_location.lng]);
    }
    // If rider is en route to dropoff (use live position)
    else if (riderPosition && trackingData.order_status === 'in_progress') {
      points.push([riderPosition.lat, riderPosition.lng]);
      points.push([trackingData.dropoff_location.lat, trackingData.dropoff_location.lng]);
    }
    // Default: show route from pickup to dropoff
    else {
      points.push([trackingData.pickup_location.lat, trackingData.pickup_location.lng]);
      points.push([trackingData.dropoff_location.lat, trackingData.dropoff_location.lng]);
    }

    return points;
  };

  if (loading && !trackingData) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  if (error && !trackingData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800">{error}</p>
        <button
          onClick={() => fetchTrackingData()}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!trackingData) return null;

  const currentETA = ['assigned', 'accepted'].includes(trackingData.order_status)
    ? trackingData.rider_to_pickup_eta
    : trackingData.rider_to_dropoff_eta;

  return (
    <div className="space-y-4">
      {/* Status Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`px-4 py-2 rounded-lg border font-medium ${getStatusColor(trackingData.order_status)}`}>
              {trackingData.order_status.toUpperCase().replace('_', ' ')}
            </div>
            {trackingData.rider_location_age_seconds !== undefined && (
              <span className="text-sm text-gray-500">
                {trackingData.rider_location_age_seconds < 60
                  ? `Updated ${trackingData.rider_location_age_seconds}s ago`
                  : `Updated ${Math.floor(trackingData.rider_location_age_seconds / 60)}m ago`}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-green-500' : connectionStatus === 'slow' ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
            <span className="text-sm text-gray-600 capitalize">{connectionStatus}</span>
          </div>
        </div>

        {/* ETA Display */}
        {currentETA && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3 bg-sky-50 rounded-lg p-4">
              <ClockIcon className="w-8 h-8 text-sky-600" />
              <div>
                <p className="text-xs text-gray-600">ETA</p>
                <p className="text-lg font-bold text-sky-900">
                  {formatETA(currentETA.duration_in_traffic_minutes)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3 bg-orange-50 rounded-lg p-4">
              <MapPinIcon className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-xs text-gray-600">Distance</p>
                <p className="text-lg font-bold text-orange-900">
                  {formatDistance(currentETA.distance_km)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3 bg-purple-50 rounded-lg p-4">
              <TruckIcon className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-xs text-gray-600">
                  {['assigned', 'accepted'].includes(trackingData.order_status) ? 'To Pickup' : 'To Dropoff'}
                </p>
                <p className="text-lg font-bold text-purple-900">
                  {new Date(currentETA.eta_timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rider Info */}
        {trackingData.rider_info && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Dispatcher Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
              <p><span className="text-gray-600">Name:</span> <span className="font-medium">{trackingData.rider_info.name}</span></p>
              <p><span className="text-gray-600">Phone:</span> <span className="font-medium">{trackingData.rider_info.phone}</span></p>
              <p><span className="text-gray-600">Vehicle:</span> <span className="font-medium capitalize">{trackingData.rider_info.vehicle_type}</span></p>
            </div>
          </div>
        )}
      </div>

      {/* Map Display */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{ height: '500px' }}>
        <MapContainer
          center={getMapCenter()}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController center={getMapCenter()} zoom={13} />

          {/* Rider Marker - Uses live position for smooth updates */}
          {riderPosition && (
            <Marker
              position={[riderPosition.lat, riderPosition.lng]}
              icon={riderIcon}
            >
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">Dispatcher Location</p>
                  {trackingData?.rider_info && <p>{trackingData.rider_info.name}</p>}
                  <p className="text-xs text-gray-500 mt-1">Live tracking</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Pickup Marker */}
          <Marker
            position={[trackingData.pickup_location.lat, trackingData.pickup_location.lng]}
            icon={pickupIcon}
          >
            <Popup>
              <div className="text-sm">
                <p className="font-semibold">Pickup Location</p>
                <p className="text-gray-600">{trackingData.pickup_location.address}</p>
              </div>
            </Popup>
          </Marker>

          {/* Dropoff Marker */}
          <Marker
            position={[trackingData.dropoff_location.lat, trackingData.dropoff_location.lng]}
            icon={dropoffIcon}
          >
            <Popup>
              <div className="text-sm">
                <p className="font-semibold">Dropoff Location</p>
                <p className="text-gray-600">{trackingData.dropoff_location.address}</p>
              </div>
            </Popup>
          </Marker>

          {/* Route Polyline */}
          {getRoutePolyline().length > 0 && (
            <Polyline
              positions={getRoutePolyline()}
              pathOptions={{ color: '#009FE3', weight: 4, opacity: 0.7 }}
            />
          )}
        </MapContainer>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Progress</h3>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
          <div className="space-y-6">
            {/* Pickup */}
            <div className="relative flex items-start space-x-4">
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                ['accepted', 'in_progress', 'completed'].includes(trackingData.order_status)
                  ? 'bg-sky-600 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}>
                {['accepted', 'in_progress', 'completed'].includes(trackingData.order_status) ? (
                  <CheckCircleIcon className="w-5 h-5" />
                ) : (
                  <span className="text-sm font-medium">1</span>
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Pickup</p>
                <p className="text-sm text-gray-600">{trackingData.pickup_location.address}</p>
                {trackingData.rider_to_pickup_eta && (
                  <p className="text-xs text-sky-600 mt-1">
                    ETA: {formatETA(trackingData.rider_to_pickup_eta.duration_in_traffic_minutes)}
                  </p>
                )}
              </div>
            </div>

            {/* Dropoff */}
            <div className="relative flex items-start space-x-4">
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                trackingData.order_status === 'completed'
                  ? 'bg-green-600 text-white'
                  : trackingData.order_status === 'in_progress'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}>
                {trackingData.order_status === 'completed' ? (
                  <CheckCircleIcon className="w-5 h-5" />
                ) : (
                  <span className="text-sm font-medium">2</span>
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Delivery</p>
                <p className="text-sm text-gray-600">{trackingData.dropoff_location.address}</p>
                {trackingData.rider_to_dropoff_eta && (
                  <p className="text-xs text-purple-600 mt-1">
                    ETA: {formatETA(trackingData.rider_to_dropoff_eta.duration_in_traffic_minutes)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Last Updated Info */}
      {lastUpdateTime && (
        <p className="text-sm text-gray-500 text-center">
          Last updated: {lastUpdateTime.toLocaleTimeString()}
          {autoRefresh && ` • Auto-refreshing every ${refreshInterval / 1000}s`}
        </p>
      )}
    </div>
  );
};

export default LiveTracking;
